/**
 * Browser side of server question delivery and grading (answer-key leak fix).
 *
 * The browser never holds an answer key before a sitting is graded: /api/quiz-items returns
 * display fields only, /api/grade-sitting marks the answers, and keys come back only to
 * entitled accounts. See netlify/functions/quiz-items.js, grade-sitting.js, answer-key.js.
 */
import { auth } from '../firebase';
import { itemRef } from './itemRef';

export class QuizServerError extends Error {
  constructor(message, status = 0, body = null) {
    super(message);
    this.name = 'QuizServerError';
    this.status = status;
    this.body = body;
  }
}

/** uid a sitting token was signed for ('' for guest sittings or an unreadable token). Not verified: display/routing only. */
export function sittingTokenOwner(token) {
  try {
    const body = String(token || '').split('.')[0].replace(/-/g, '+').replace(/_/g, '/');
    const json = JSON.parse(atob(body + '='.repeat((4 - (body.length % 4)) % 4)));
    return typeof json?.uid === 'string' ? json.uid : '';
  } catch {
    return '';
  }
}

/** Non-secret sitting identity for exact resume links; server verifies the signature. */
export function sittingTokenId(token) {
  try {
    const body = String(token || '').split('.')[0].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(body + '='.repeat((4 - body.length % 4) % 4))).sid || '';
  } catch { return ''; }
}

/** Signed server scope decoded for display/resume only; enforcement stays on the server. */
export function sittingTokenIsPreview(token) {
  try {
    const body = String(token || '').split('.')[0].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(body + '='.repeat((4 - body.length % 4) % 4))).k === 'preview';
  } catch { return false; }
}

async function authHeader() {
  const user = auth.currentUser;
  if (!user) return {};
  try {
    return { Authorization: `Bearer ${await user.getIdToken()}` };
  } catch {
    return {};
  }
}

async function post(path, body, { fetchImpl = typeof fetch === 'function' ? fetch : null } = {}) {
  if (!fetchImpl) throw new QuizServerError('Network unavailable.');
  let res;
  try {
    res = await fetchImpl(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
      body: JSON.stringify(body),
    });
  } catch {
    throw new QuizServerError('Could not reach the server. Check your connection and try again.');
  }
  let data = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }
  if (!res.ok) {
    throw new QuizServerError(data?.error || 'Could not load the item bank. Try again in a moment.', res.status, data);
  }
  return data;
}

/** { items, token, sid, kind, level, formId, durationSeconds } */
export function fetchSittingItems(params, opts) {
  return post('/api/quiz-items', params, opts);
}

/** { sid, score, results[], keyRevealed, flags } */
export async function gradeSittingOnServer({ token, answers }, opts) {
  const result = await post('/api/grade-sitting', { token, answers }, opts);
  if (result?.kind !== 'preview' && auth.currentUser?.emailVerified && result?.sid) {
    import('./referrals').then(({ getReferralCode, referralRequest }) => {
      if (getReferralCode()) return referralRequest('mock_completed', { sittingId: result.sid });
      return null;
    }).catch(() => {});
  }
  return result;
}

/** Keys for a sitting grade-sitting already stored: { keys: { [ref]: { correctAnswer, explanation?, rubric? } } } */
export function fetchAnswerKeys({ sid }, opts) {
  return post('/api/answer-key', { sid }, opts);
}

/** Admin only: the full L1 bank with keys (admin question pages). */
export async function loadAdminQuestionBank(opts) {
  const data = await post('/api/admin-question-bank', {}, opts);
  return Array.isArray(data?.items) ? data.items : [];
}

/** The server ref for a stored question (served items carry it; older rows are derived). */
export function refForQuestion(level, question) {
  return question?.ref || itemRef(level, question);
}

/**
 * Merge grade-sitting marks into the served questions so Results and the stats code keep
 * using `answersMatch(answer, question.correctAnswer)`:
 * - key revealed → the real correctAnswer / explanation / rubric;
 * - key hidden → correctAnswer is the learner's own answer when right, null when wrong, so the
 *   score is right but no wrong item exposes its key.
 */
export function applyGradeToQuestions(questions = [], answers = [], grade = {}) {
  const results = Array.isArray(grade.results) ? grade.results : [];
  return questions.map((q, i) => {
    const r = results[i] || {};
    if (r.correctAnswer !== undefined) {
      const { correctAnswer, explanation, rubric } = r;
      return {
        ...q,
        correctAnswer: String(correctAnswer),
        ...(explanation !== undefined ? { explanation } : {}),
        ...(rubric !== undefined ? { rubric } : {}),
        keyHidden: false,
      };
    }
    return {
      ...q,
      correctAnswer: r.correct === true ? String(answers[i]) : null,
      keyHidden: true,
    };
  });
}

/**
 * The answers the server actually graded. A retried submit (the first response was lost) gets
 * the stored sitting back, so any answer changed since then must follow the server's copy or
 * Results would score answers the server never marked. Unchanged answers keep their local form.
 */
export function reconcileAnswers(local = [], server) {
  if (!Array.isArray(server)) return local;
  return server.map((s, i) => {
    const mine = local[i];
    const norm = (v) => (v === null || v === undefined || v === '' ? null : String(v));
    return norm(mine) === norm(s) ? mine ?? null : norm(s);
  });
}

/** Merge /api/answer-key results into stored questions (attempt review for entitled users). */
export function applyKeysToQuestions(level, questions = [], keys = {}) {
  return questions.map((q) => {
    const key = keys[refForQuestion(level, q)];
    return key ? { ...q, ...key, keyHidden: false } : q;
  });
}

/** Questions in a stored attempt that still lack their key. */
export function questionsMissingKeys(questions = []) {
  return questions.filter((q) => q && (q.keyHidden === true || q.correctAnswer === undefined || q.correctAnswer === null));
}

/** Older builds cached the whole bank, keys included, in web storage. */
export function purgeLegacyBankCaches() {
  if (typeof window === 'undefined') return;
  ['cfa_questions_bank_v5', 'cfa_questions_bank_v4'].forEach((key) => {
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* private mode */
    }
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      /* private mode */
    }
  });
}
